import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import {
Box,
Button,
TextField,
Typography,
Paper,
IconButton,
InputAdornment,
useTheme,
useMediaQuery
} from '@mui/material';
import {
Visibility,
VisibilityOff,
Login as LoginIcon,
Lock as LockIcon,
Email as EmailIcon
} from '@mui/icons-material';
import useCustomToast from '../hooks/useToast';
import { APIUserLogin } from '../services/APIs';
import { TEXT_CONNECTION_ERROR, TEXT_ERROR_INPUTS_CONTACTS } from '../services/Text';
import { companyInfo } from '../utils/dataInfo';
import { bgIcon } from '../assets';

const Login = () => {
const navigate = useNavigate();
const showToast = useCustomToast();
const theme = useTheme();
const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
const isTablet = useMediaQuery(theme.breakpoints.down('md'));

const [formData, setFormData] = useState({
    email: '',
    password: '',
    showPassword: false
});

const [loading, setLoading] = useState(false);
const [errors, setErrors] = useState({
    email: '',
    password: ''
});

const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
    ...prev,
    [name]: value
    }));

    // Clear error when typing
    if (errors[name]) {
    setErrors(prev => ({
        ...prev,
        [name]: ''
    }));
    }
};

const togglePasswordVisibility = () => {
    setFormData(prev => ({
    ...prev,
    showPassword: !prev.showPassword
    }));
};

const validateForm = () => {
    let valid = true;
    const newErrors = {
    email: '',
    password: ''
    };

    if (!formData.email) {
    newErrors.email = 'Email is required';
    valid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
    newErrors.email = 'Please enter a valid email';
    valid = false;
    }

    if (!formData.password) {
    newErrors.password = 'Password is required';
    valid = false;
    }

    setErrors(newErrors);
    return valid;
};

const handleLogin = async () => {
    if (!validateForm()) {
    showToast("Warning", TEXT_ERROR_INPUTS_CONTACTS, "warning");
    return;
    }

    setLoading(true);

    try {
    const response = await axios.post(APIUserLogin, {
        email: formData.email,
        password: formData.password
    });

    if (response.data.status === "success") {
        const userData = response.data.data;

        const userInfo = {
        name: userData.name,
        id: userData.id,
        email: userData.email,
        role: userData.role,
        branch_id: userData.branch_id,
        user_type:userData.user_type,
        branch_name: userData.branch_name,
        counter_id:userData.counter_id,
        hotel_role:userData.hotel_role,
        status: userData.status,
        };
        sessionStorage.setItem('userInfo', JSON.stringify(userInfo));
        showToast("Success", "Login successful", "success");
        navigate("/dashboard");

    } else {
        showToast("Error", response.data.message || "Login failed", "error");
    }
    } catch (error) {
    if (error.response) {
        // Handle validation errors (422 status)
        if (error.response.status === 422) {
        const errors = error.response.data.errors;
        let errorMessages = [];
        // Collect all validation error messages
        for (const field in errors) {
            errorMessages.push(...errors[field]);
        }

        // Show all error messages in toast
        showToast(
            "Validation Error",
            errorMessages.join("\n"),
            "error"
        );
        }
        // Handle other API errors
        else {
        showToast(
            "Error",
            error.response.data.message || "An error occurred",
            "error"
        );
        }
    }
    // Handle network errors
    else if (error.code === "ERR_NETWORK") {
        showToast("Connection Error", TEXT_CONNECTION_ERROR, "error");
    }
    // Handle other unexpected errors
    else {
        showToast("Error", "An unexpected error occurred", "error");
    }
    } finally {
    setLoading(false);
    }
};

return (
    <Box sx={{
    display: 'flex',
    minHeight: '100vh',
    position: 'relative',
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
    p: isMobile ? 1 : 2,
    backgroundColor: '#f5f7fa'
    }}>
    {/* Professional Background with Overlay */}
    <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 0,
        backgroundImage: bgIcon ? `url(${bgIcon})` : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        backgroundSize: isMobile ? 'cover' : 'cover',
        backgroundPosition: isMobile ? 'center' : 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed',
        '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.15) 0%, rgba(33, 150, 243, 0.1) 100%)',
        },
        '&::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(to bottom, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.6) 100%)',
        mixBlendMode: 'overlay'
        }
    }} />

    {/* Decorative Elements */}
    <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 1,
        opacity: 0.05,
        background: 'radial-gradient(circle at 20% 80%, rgba(25, 118, 210, 0.4) 0%, transparent 50%)',
        ...(isMobile && {
        background: 'radial-gradient(circle at center, rgba(25, 118, 210, 0.3) 0%, transparent 70%)'
        })
    }} />

    <Paper elevation={isMobile ? 3 : 6} sx={{
        position: 'relative',
        zIndex: 2,
        width: '100%',
        maxWidth: isMobile ? '95%' : isTablet ? 400 : 450,
        p: isMobile ? 2 : { xs: 3, sm: 4 },
        borderRadius: 3,
        backgroundColor: 'rgba(255, 255, 255, 0.98)',
        backdropFilter: 'blur(10px)',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
        border: '1px solid rgba(255, 255, 255, 0.3)',
        transform: isMobile ? 'none' : 'translateY(-10px)',
        transition: 'all 0.3s ease',
        '&:hover': !isMobile && {
        transform: 'translateY(-12px)',
        boxShadow: '0 25px 80px rgba(0, 0, 0, 0.2)'
        }
    }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Typography variant={isMobile ? 'h5' : 'h4'} component="h1" sx={{
            fontWeight: 'bold',
            mb: 2,
            background: 'linear-gradient(135deg, #1976d2 0%, #2196f3 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            letterSpacing: '-0.5px'
        }}>
            Welcome Back!
        </Typography>
        
        {/* Company Logo Image */}
        {companyInfo.logo && (
            <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center',
            mb: 2,
            position: 'relative'
            }}>
            <Box sx={{
                position: 'absolute',
                top: -10,
                left: 0,
                right: 0,
                height: isMobile ? 120 : 150,
                background: 'radial-gradient(circle, rgba(25, 118, 210, 0.1) 0%, transparent 70%)',
                zIndex: 0
            }} />
            <img 
                src={companyInfo.logo} 
                alt={`${companyInfo.name} Logo`}
                style={{ 
                height: isMobile ? '80px' : '100px',
                width: 'auto',
                maxWidth: '100%',
                objectFit: 'contain',
                position: 'relative',
                zIndex: 1,
                filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))'
                }}
            />
            </Box>
        )}

        <Typography variant="subtitle1" sx={{ 
            color: 'text.secondary', 
            mt: 1,
            fontWeight: 500,
            fontSize: isMobile ? '0.9rem' : '1rem'
        }}>
            Log in to your account
        </Typography>
        </Box>

        <Box component="form" sx={{ mt: 2 }}>
        <TextField
            fullWidth
            label="Email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            error={!!errors.email}
            helperText={errors.email}
            margin="normal"
            InputProps={{
            startAdornment: (
                <InputAdornment position="start">
                <EmailIcon color="primary" />
                </InputAdornment>
            ),
            }}
            sx={{ 
            mb: 2,
            '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                transition: 'all 0.2s ease',
                '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.05)'
                }
            }
            }}
            size={isMobile ? 'small' : 'medium'}
        />

        <TextField
            fullWidth
            label="Password"
            name="password"
            type={formData.showPassword ? 'text' : 'password'}
            value={formData.password}
            onChange={handleChange}
            error={!!errors.password}
            helperText={errors.password}
            margin="normal"
            InputProps={{
            startAdornment: (
                <InputAdornment position="start">
                <LockIcon color="primary" />
                </InputAdornment>
            ),
            endAdornment: (
                <InputAdornment position="end">
                <IconButton
                    onClick={togglePasswordVisibility}
                    edge="end"
                    size={isMobile ? 'small' : 'medium'}
                >
                    {formData.showPassword ? <VisibilityOff color="primary" /> : <Visibility color="primary" />}
                </IconButton>
                </InputAdornment>
            ),
            }}
            sx={{ 
            mb: 1,
            '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                transition: 'all 0.2s ease',
                '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.05)'
                }
            }
            }}
            size={isMobile ? 'small' : 'medium'}
        />

        <Box sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mb: 3
        }}>
            <Link to="/forget-password" style={{ textDecoration: 'none' }}>
            <Typography variant="body2" color="primary" sx={{ fontWeight: 500, fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
                Forgot password?
            </Typography>
            </Link>
        </Box>

        <Button
            fullWidth
            variant="contained"
            color="primary"
            onClick={handleLogin}
            disabled={loading}
            startIcon={<LoginIcon />}
            sx={{
            py: isMobile ? 1.2 : 1.5,
            mb: 2,
            fontSize: isMobile ? '0.9rem' : '1rem',
            borderRadius: 2,
            fontWeight: 'bold',
            textTransform: 'none',
            background: 'linear-gradient(135deg, #1976d2 0%, #2196f3 100%)',
            '&:hover': {
                background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(25, 118, 210, 0.4)'
            },
            '&:active': {
                transform: 'translateY(0)'
            },
            transition: 'all 0.2s ease'
            }}
            size={isMobile ? 'medium' : 'large'}
        >
            {loading ? 'Logging in...' : 'Log In'}
        </Button>

        {/* decorative elements */}
        <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            mt: 2,
            pt: 2,
            borderTop: '1px solid',
            borderColor: 'divider'
        }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}>
            {companyInfo.name || 'Management System'}
            </Typography>
        </Box>
        </Box>
    </Paper>
    </Box>
);
};

export default Login;